/* @global googletag */

// local
import { loadScript, uniqueId, getCookie } from './utils';

const defaultSize = [320, 50];

const sizeMapping = [
  [
    [1024, 768],
    [
      [970, 250],
      [970, 90],
    ],
  ],
  [[980, 690], [[728, 90]]],
  [
    [640, 480],
    [
      [320, 100],
      [320, 50],
    ],
  ],
];

export class AdLoader {
  constructor({
    adSection = 'california',
    idPrefix = 'dg-gpt-',
    selector = '.dg-gpt-ad',
  } = {}) {
    // set up variables
    this.adUnitPath = `/21787098806/web.latimes/${adSection}`;
    this.idPrefix = idPrefix;
    this.selector = selector;

    // bind our functions
    this.handleIntersections = this.handleIntersections.bind(this);
  }
  init() {
    // see if googletag already exists and if not, create it
    window.googletag = window.googletag || { cmd: [] };

    // we just kick this off - we don't care when it happens
    loadScript(
      'https://securepubads.g.doubleclick.net/tag/js/gpt.js'
    ).catch(() => {});

    // kick off the setup of the GPT service
    this.setupGpt();

    // create the intersection observer for lazy loading ads
    this.initIntersectionObserver();

    // set up the current ad containers
    this.setupAdContainers();
  }

  setupGpt() {
    googletag.cmd.push(() => {
      googletag.pubads().enableSingleRequest();
      googletag.pubads().disableInitialLoad();

      if (navigator.globalPrivacyControl || getCookie('c_rdp') === '1') {
        googletag.pubads().setPrivacySettings({
          restrictDataProcessing: true,
        });
      }

      googletag.enableServices();
    });
  }

  setupAdContainers() {
    const containers = document.querySelectorAll(this.selector);

    for (let idx = 0; idx < containers.length; idx++) {
      const container = containers[idx];

      if (!container.hasAttribute('id')) {
        const containerId = uniqueId(this.idPrefix);
        container.setAttribute('id', containerId);

        this.observer.observe(container);
      }
    }
  }

  initIntersectionObserver() {
    // config for the IntersectionObserver
    const config = {
      rootMargin: '100% 0px',
    };

    this.observer = new IntersectionObserver(this.handleIntersections, config);
  }

  handleIntersections(entries) {
    entries.forEach((entry) => {
      const target = entry.target;

      if (entry.isIntersecting) {
        // kick off the ad
        this.renderAd(target);

        // we don't need to watch it anymore
        this.observer.unobserve(target);
      }
    });
  }

  renderAd(element) {
    // get the element's id
    const elementId = element.getAttribute('id');

    googletag.cmd.push(() => {
      const slot = googletag.defineSlot(
        this.adUnitPath,
        defaultSize,
        elementId
      );

      slot.defineSizeMapping(sizeMapping);

      // if it fails let it collapse
      slot.setCollapseEmptyDiv(true);

      slot.addService(googletag.pubads());

      // Display has to be called before
      // refresh and after the slot div is in the page.
      googletag.display(elementId);
      googletag.pubads().refresh([slot]);
    });
  }

  static createAdContainer() {
    // <div class="dg-gpt-ad-container">
    //   <div class="dg-gpt-ad"></div>
    // </div>

    const container = document.createElement('div');
    container.classList.add('dg-gpt-ad-container');

    const slot = document.createElement('div');
    slot.classList.add('dg-gpt-ad');

    container.appendChild(slot);

    return container;
  }

  reset() {
    // tell the IntersectionObserver to purge all of its targets
    this.observer.disconnect();

    // tell GPT to stop caring about these slots
    googletag.cmd.push(() => {
      googletag.destroySlots();
    });
  }
}
